// Ren logik för köttetiketterna (ET2): antal per styckdetalj → etiketter, ark och zoom.

/** Styckdetaljerna i steg 2 (färs och grytbitar har eget steg). */
export const CUT_LIST = [
  'Innanlår','Ytterlår','Stek','Rostbiff','Rulle','Ryggbiff',
  'Filé','Bogstek','Sida','Fransyska','Hjärta','Lever','Njure',
  'Hals','Revbensspjäll','Hel kropp',
];

/** Förval: två av varje huvuddetalj, inga inälvor. */
export const DEFAULT_CUT_COUNTS: Record<string, number> = {
  'Innanlår': 2, 'Ytterlår': 2, 'Stek': 2, 'Rostbiff': 2, 'Rulle': 2,
  'Ryggbiff': 2, 'Filé': 2, 'Bogstek': 2, 'Sida': 2,
};

export const MINCE = 'Färs';
export const STEW = 'Grytbitar';

export interface CutCount { cut: string; count: number }

/** Ordningen på arket: styckdetaljerna först, sedan grytbitar och färs. */
export function cutSequence(cuts: CutCount[], stew: number, mince: number): string[] {
  const seq: string[] = [];
  for (const { cut, count } of cuts) for (let i = 0; i < clampCount(count); i++) seq.push(cut);
  for (let i = 0; i < clampCount(stew); i++) seq.push(STEW);
  for (let i = 0; i < clampCount(mince); i++) seq.push(MINCE);
  return seq;
}

export function clampCount(n: number): number {
  return Math.max(0, Math.min(99, Math.round(Number.isFinite(n) ? n : 0)));
}

/**
 * Bygger om etiketterna efter en ny sekvens men behåller det användaren redan skrivit
 * (vikt, anteckning …) på etiketter med samma styckdetalj, i ordning.
 */
export function reconcileLabels<L extends { cut: string }>(
  prev: L[], seq: string[], make: (cut: string) => L,
): L[] {
  const pool = new Map<string, L[]>();
  for (const l of prev) {
    const list = pool.get(l.cut);
    if (list) list.push(l); else pool.set(l.cut, [l]);
  }
  return seq.map(cut => pool.get(cut)?.shift() ?? make(cut));
}

// ── Ark och etikettskrivare ────────────────────────────────────────────────

export type PrintOutput = 'sheet' | 'printer';

export interface SheetFormat { cols: number; rows: number }
export const DEFAULT_SHEET: SheetFormat = { cols: 2, rows: 5 };
export const SHEET_PRESETS: SheetFormat[] = [
  { cols: 2, rows: 5 }, { cols: 2, rows: 4 }, { cols: 2, rows: 7 }, { cols: 3, rows: 7 },
];

export interface PrinterSize { id: string; label: string; wMm: number; hMm: number }
export const PRINTER_SIZES: PrinterSize[] = [
  { id: '90x55', label: '90 × 55 mm', wMm: 90, hMm: 55 },
  { id: '100x62', label: '100 × 62 mm (Brother 62 mm)', wMm: 100, hMm: 62 },
  { id: '102x76', label: '102 × 76 mm (Zebra/Dymo 4")', wMm: 102, hMm: 76 },
];

export function chunk<T>(items: T[], size: number): T[][] {
  const n = Math.max(1, size);
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += n) out.push(items.slice(i, i + n));
  return out;
}

// ── Nypzoom ────────────────────────────────────────────────────────────────

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 5;

export function clampZoom(z: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
}

/** Panorering begränsas så att innehållet alltid täcker rutan. */
export function clampPan(x: number, y: number, zoom: number, w: number, h: number): { x: number; y: number } {
  const minX = Math.min(0, w - w * zoom);
  const minY = Math.min(0, h - h * zoom);
  return { x: Math.max(minX, Math.min(0, x)), y: Math.max(minY, Math.min(0, y)) };
}

/**
 * Ny zoom runt en fast punkt (fingrarnas mittpunkt, i rutans koordinater):
 * punkten under fingrarna ligger kvar på samma ställe.
 */
export function zoomAround(
  view: { zoom: number; x: number; y: number }, nextZoom: number, px: number, py: number,
): { zoom: number; x: number; y: number } {
  const zoom = clampZoom(nextZoom);
  const k = zoom / view.zoom;
  return { zoom, x: px - (px - view.x) * k, y: py - (py - view.y) * k };
}

export const MM_TO_PX = 96 / 25.4;

// ── Kilopris, vikt och pris (ET5) ─────────────────────────────────────────

/** 'value' = skrivs ut, 'blank' = tom ruta att fylla i för hand, 'hidden' = visas inte. */
export type PriceFieldMode = 'value' | 'blank' | 'hidden';
export interface PriceField { mode: PriceFieldMode; text: string }
export interface LabelPricing { kgPrice: PriceField; weight: PriceField; price: PriceField; priceComputed: boolean }

export function parseNum(v: string | undefined | null): number {
  const s = String(v ?? '').replace(/\s/g, '').replace(',', '.');
  return s ? Number(s) : NaN;
}

/**
 * Vad etiketten visar. Kilopris och vikt ger priset (avrundat till hela kronor);
 * annars visas ett angivet pris. Skrivs vikten för hand blir viktrutan tom, och
 * priset också om det skulle räknas fram ur vikten.
 */
export function labelPricing(
  l: { kgPrice?: string; weightKg?: string; totalPrice?: string }, weightByHand: boolean,
): LabelPricing {
  const kgText = (l.kgPrice ?? '').trim();
  const wText = weightByHand ? '' : (l.weightKg ?? '').trim();
  const manual = (l.totalPrice ?? '').trim();
  const kg = parseNum(kgText); const w = parseNum(wText);
  const field = (text: string, blank: boolean): PriceField =>
    text ? { mode: 'value', text } : { mode: blank ? 'blank' : 'hidden', text: '' };

  const computed = Number.isFinite(kg) && Number.isFinite(w) && kg > 0 && w > 0;
  const price = computed ? String(Math.round(kg * w)) : manual;
  return {
    kgPrice: field(kgText, false),
    weight: field(wText, weightByHand),
    price: field(price, weightByHand && !!kgText),
    priceComputed: computed,
  };
}

// ── QR-länk och sparande (ET6) ────────────────────────────────────────────

/** QR-koden på varje etikett: en länk till etikettens egna sparade uppgifter. */
export function labelQrUrl(id: string): string {
  return `https://qvenari.com/?labelId=${encodeURIComponent(id)}`;
}

/** Id ur en skannad QR: ?labelId=, äldre /v/:id-länkar, annars null. */
export function labelIdFromUrl(href: string): string | null {
  try {
    const u = new URL(href);
    const q = u.searchParams.get('labelId');
    if (q) return q;
    const m = u.pathname.match(/^\/v\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

/** De etiketter vars sparade innehåll ändrats sedan förra sparningen (nyckel = id). */
export function changedSince<T extends { id: string }>(items: T[], saved: Map<string, string>): T[] {
  return items.filter(i => saved.get(i.id) !== JSON.stringify(i));
}

// ── Spårbarhet (ET7) ──────────────────────────────────────────────────────

/** "Lars Andersson" → "Lars A." (samma regel som API:ts publika spårbarhet). */
export function shortPersonName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? '';
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

/** Etiketten och QR-sidan är publika: "Skytt: Förnamn Efternamn" i anteckningen kortas. */
export function publicNotes(notes: string): string {
  return notes.replace(/Skytt:\s*([^•]+?)(\s*•|$)/g, (_m, n: string, end: string) => `Skytt: ${shortPersonName(n)}${end}`);
}
